// ============================================================================
// UnusedMealReportScreen — Reports → Unused Package Meals (buffet slice 4)
// ============================================================================
// Plan meals guests were entitled to and did not take, over past dates: each
// allowance's guests less those the restaurant marked served or packed. "Not
// coming" is shown (a reason) but is still unused. Unused meals move no revenue
// (decision M5), so this is the only place they appear. Counts only. The period
// ends at the day before the property's business date: today's meals may still
// be eaten.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { EnterpriseScreenShell } from 'components/enterprise-shell';
import ReportHeader, { ReportBody, type ReportHeaderItem } from 'components/reports/ReportHeader';
import type { ActionButton } from 'components/modals';
import { SyutGrid, type SyutGridColumn, SyutSelect, type SelectOption } from 'components/ui';
import { mealForecastService } from 'api/services/mealForecastService';
import { propertyService } from 'api/services/propertyService';
import notificationService from 'services/notificationService';
import { downloadCsv } from 'utils/csvExport';
import { paxText } from 'utils/mealForecast';
import { useDefaultProperty } from 'hooks/api/useDefaultProperty';
import { usePropertyLabel } from 'hooks/api/usePropertyLabel';
import { ALLOWANCE_LABEL, type UnusedMealReport, type UnusedMealRow, type UnusedMealSummary } from 'api/types/mealForecast.types';
import 'components/modules/dashboard-module/dashboard.css';

const isoOf = (d: Date) => new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
const yesterdayIso = () => { const d = new Date(); d.setDate(d.getDate() - 1); return isoOf(d); };
const monthStartIso = () => { const d = new Date(); d.setDate(d.getDate() - 1); d.setDate(1); return isoOf(d); };
const formatDate = (iso?: string | null) =>
  iso ? new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '-';
const muted: React.CSSProperties = { color: 'var(--text-muted)' };

interface Props {
  moduleIcon?: string;
  tabId?: string;
  moduleName?: string;
  /** RBAC — {'view','export'}. Fail-closed: [] renders no export. */
  allowedActions?: string[];
}

type Row = UnusedMealRow & { id: string };
type SummaryRow = UnusedMealSummary & { id: string };

const UnusedMealReportScreen: React.FC<Props> = ({ tabId, allowedActions = [] }) => {
  const [propertyId, setPropertyId] = useState('');
  const { defaultPropertyId } = useDefaultProperty();
  useEffect(() => { if (!propertyId && defaultPropertyId) setPropertyId(defaultPropertyId); }, [defaultPropertyId, propertyId]);
  const [from, setFrom] = useState(monthStartIso());
  const [to, setTo] = useState(yesterdayIso());
  const [view, setView] = useState<'rows' | 'summary'>('rows');
  const [filterOpen, setFilterOpen] = useState(false);
  const propertyLabelsRef = useRef<Record<string, string>>({});
  const fetchedPropertyLabel = usePropertyLabel(propertyId);
  const propertyLabel = fetchedPropertyLabel || propertyLabelsRef.current[propertyId] || '';

  const enabled = !!propertyId && !!from && !!to && from <= to;
  const [report, setReport] = useState<UnusedMealReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const load = useCallback(async () => {
    if (!enabled) { setReport(null); return; }
    setLoading(true);
    setError(null);
    try {
      setReport(await mealForecastService.getUnused(propertyId, from, to));
    } catch (err: any) {
      setReport(null);
      setError(err?.response?.data?.message || err?.message || 'The report did not load.');
    } finally {
      setLoading(false);
    }
  }, [enabled, propertyId, from, to]);
  useEffect(() => { void load(); }, [load]);

  const loadProperties = useCallback(async (q: string): Promise<SelectOption[]> => {
    const res = await propertyService.getAll({ searchTerm: q || undefined, pageSize: q ? 50 : 500, includeInactive: false });
    const opts = (res.data ?? []).map((p: any) => ({ value: p.id, label: `${p.propertyCode} - ${p.propertyName}` }));
    opts.forEach((o: SelectOption) => { propertyLabelsRef.current[o.value as string] = o.label; });
    return opts;
  }, []);

  const rows = useMemo<Row[]>(() => (report?.rows ?? []).map((r) => ({ ...r, id: `${r.date}-${r.bookingId}-${r.allowance}-${r.rooms}` })), [report]);
  const summary = useMemo<SummaryRow[]>(() => (report?.summary ?? []).map((s) => ({ ...s, id: `${s.planCode}-${s.allowance}` })), [report]);
  const totals = useMemo(() => summary.reduce((t, s) => ({ entitled: t.entitled + s.entitled, used: t.used + s.used, unused: t.unused + s.unused }),
    { entitled: 0, used: 0, unused: 0 }), [summary]);

  const columns = useMemo<SyutGridColumn<Row>[]>(() => [
    { id: 'date', header: 'Date', accessorKey: 'date', size: 110, cell: ({ row }) => formatDate(row.original.date) },
    { id: 'rooms', header: 'Room', accessorKey: 'rooms', size: 90 },
    {
      id: 'guestName', header: 'Guest / Booking', accessorKey: 'guestName', size: 200,
      cell: ({ row }) => <span>{row.original.guestName || '—'}<div style={{ fontSize: '0.72rem', ...muted }}>{row.original.bookingNumber}</div></span>,
    },
    { id: 'planCode', header: 'Plan', accessorKey: 'planCode', size: 80 },
    { id: 'allowance', header: 'Meal', accessorKey: 'allowance', size: 170, cell: ({ row }) => ALLOWANCE_LABEL[row.original.allowance] ?? row.original.allowance },
    { id: 'entitled', header: 'Entitled', accessorKey: 'entitledAdults', size: 85, cell: ({ row }) => paxText(row.original.entitledAdults, row.original.entitledChildren) },
    { id: 'used', header: 'Used', accessorKey: 'usedAdults', size: 75, cell: ({ row }) => (row.original.usedAdults + row.original.usedChildren) ? paxText(row.original.usedAdults, row.original.usedChildren) : <span style={muted}>—</span> },
    {
      id: 'notComing', header: 'Not coming', accessorKey: 'notComingAdults', size: 95,
      cell: ({ row }) => (row.original.notComingAdults + row.original.notComingChildren) ? paxText(row.original.notComingAdults, row.original.notComingChildren) : <span style={muted}>—</span>,
    },
    { id: 'unused', header: 'Unused', accessorKey: 'unusedAdults', size: 80, cell: ({ row }) => <b>{paxText(row.original.unusedAdults, row.original.unusedChildren)}</b> },
  ], []);

  const summaryColumns = useMemo<SyutGridColumn<SummaryRow>[]>(() => [
    { id: 'planCode', header: 'Plan', accessorKey: 'planCode', size: 90 },
    { id: 'planName', header: 'Plan name', accessorKey: 'planName', size: 180 },
    { id: 'allowance', header: 'Meal', accessorKey: 'allowance', size: 180, cell: ({ row }) => ALLOWANCE_LABEL[row.original.allowance] ?? row.original.allowance },
    { id: 'entitled', header: 'Entitled', accessorKey: 'entitled', size: 90 },
    { id: 'used', header: 'Used', accessorKey: 'used', size: 90 },
    { id: 'unused', header: 'Unused', accessorKey: 'unused', size: 90, cell: ({ row }) => <b>{row.original.unused}</b> },
    {
      id: 'share', header: 'Unused %', accessorKey: 'unused', size: 90,
      cell: ({ row }) => row.original.entitled ? `${Math.round((row.original.unused / row.original.entitled) * 100)}%` : '—',
    },
  ], []);

  const exportCsv = useCallback(() => {
    if (!report) { notificationService.toast.warning('Nothing to export yet.'); return; }
    if (view === 'rows')
      downloadCsv(`UnusedPackageMeals_${report.fromDate.slice(0, 10)}_${report.toDate.slice(0, 10)}.csv`,
        ['Date', 'Room', 'Guest', 'Booking', 'Plan', 'Meal', 'Entitled adults', 'Entitled children', 'Used adults', 'Used children',
          'Not coming adults', 'Not coming children', 'Unused adults', 'Unused children'],
        rows.map((r) => [r.date.slice(0, 10), r.rooms, r.guestName, r.bookingNumber, r.planCode, ALLOWANCE_LABEL[r.allowance] ?? r.allowance,
          r.entitledAdults, r.entitledChildren, r.usedAdults, r.usedChildren, r.notComingAdults, r.notComingChildren, r.unusedAdults, r.unusedChildren]));
    else
      downloadCsv(`UnusedPackageMeals_Summary_${report.fromDate.slice(0, 10)}_${report.toDate.slice(0, 10)}.csv`,
        ['Plan', 'Plan name', 'Meal', 'Entitled', 'Used', 'Unused'],
        summary.map((s) => [s.planCode, s.planName, ALLOWANCE_LABEL[s.allowance] ?? s.allowance, s.entitled, s.used, s.unused]));
  }, [report, view, rows, summary]);

  const actionButtons: ActionButton[] = useMemo(() => {
    const b: ActionButton[] = [
      { id: 'view', icon: <IconSystem.list size={16} />, label: view === 'rows' ? 'Show summary' : 'Show stays', onClick: () => setView((v) => (v === 'rows' ? 'summary' : 'rows')), variant: 'secondary' },
      { id: 'refresh', icon: <IconSystem.refresh size={16} />, label: 'Refresh', onClick: () => { void load(); }, variant: 'icon' },
    ];
    if (allowedActions.includes('export'))
      b.push({ id: 'export', icon: <IconSystem.download size={16} />, label: 'Export', tooltip: 'Export to CSV', onClick: exportCsv, variant: 'icon' });
    return b;
  }, [view, load, allowedActions, exportCsv]);

  const headerItems: ReportHeaderItem[] = [
    { label: 'Property', value: propertyLabel || (propertyId ? '…' : 'Select in filters'), primary: true },
    {
      label: 'Period', primary: true,
      value: report ? `${formatDate(report.fromDate)} – ${formatDate(report.toDate)}` : `${formatDate(from)} – ${formatDate(to)}`,
      title: 'Ends at the day before the business date: today\'s meals may still be eaten',
    },
    ...(report ? [
      { label: 'Entitled', value: totals.entitled, icon: 'users' as const },
      { label: 'Used', value: totals.used, icon: 'users' as const },
      { label: 'Unused', value: totals.unused, icon: 'users' as const },
      { label: 'Unused %', value: totals.entitled ? `${Math.round((totals.unused / totals.entitled) * 100)}%` : '—' },
    ] : []),
  ];

  return (
    <EnterpriseScreenShell
      title="Unused Package Meals"
      description="Plan meals guests did not take, by stay and by plan — counts only; unused meals move no revenue."
      icon={<IconSystem.list size={18} />}
      tabId={tabId}
      actionButtons={actionButtons}
      filterConfig={{ onClick: () => setFilterOpen((v) => !v), isActive: filterOpen, appliedCount: (from !== monthStartIso() || to !== yesterdayIso()) ? 1 : 0 }}
      trailingContent={
        filterOpen && (
          <div className="search-panel">
            <div className="search-panel-header">
              <h3 className="search-panel-title">Filters</h3>
              <button className="search-panel-close" onClick={() => setFilterOpen(false)} title="Close filters"><IconSystem.x size={14} /></button>
            </div>
            <div className="search-panel-content">
              <div className="filter-section">
                <SyutSelect label={<>Property <span className="text-danger">*</span></>} value={propertyId}
                  onChange={(v) => setPropertyId((v as string) || '')} isAsync loadOptions={loadProperties} defaultOptions cacheOptions={false}
                  isClearable={false} menuPortalTarget={document.body} size="sm" />
              </div>
              <div className="filter-section">
                <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>From <span className="text-danger">*</span></label>
                <input type="date" className="form-input" value={from} max={to} onChange={(e) => setFrom(e.target.value)} style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }} />
              </div>
              <div className="filter-section">
                <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>To <span className="text-danger">*</span></label>
                <input type="date" className="form-input" value={to} min={from} onChange={(e) => setTo(e.target.value)} style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }} />
                <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: '0.15rem' }}>Up to the day before the business date; at most 62 days.</div>
              </div>
            </div>
            <div className="filter-actions">
              <button className="btn btn-secondary" onClick={() => { setFrom(monthStartIso()); setTo(yesterdayIso()); }}>Clear</button>
              <button className="btn btn-primary" onClick={() => setFilterOpen(false)}>Apply</button>
            </div>
          </div>
        )
      }
    >
      <ReportHeader items={headerItems} />
      <ReportBody footer={report ? <span>Unused = entitled less served or packed. <b>Not coming</b> is the guest&rsquo;s answer; the meal is still unused. A MAP meal is one of lunch or dinner a day.</span> : undefined}>
        {!enabled ? (
          <div style={{ color: 'var(--text-muted)', padding: '2rem', textAlign: 'center' }}>Pick a property and a period in the filters.</div>
        ) : error ? (
          <div style={{ color: 'var(--danger, #ef4444)', padding: '2rem', textAlign: 'center' }}>{error}</div>
        ) : view === 'rows' ? (
          <SyutGrid data={rows} columns={columns} isLoading={loading} emptyMessage="Every plan meal in the period was taken." />
        ) : (
          <SyutGrid data={summary} columns={summaryColumns} isLoading={loading} emptyMessage="No plan guests in the period." />
        )}
      </ReportBody>
    </EnterpriseScreenShell>
  );
};

export default UnusedMealReportScreen;
